import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import type {
  ComisionPagoDetalle,
  ComisionResponse,
  ComisionesTotalResponse,
  Paginated,
} from '@aid/shared';

export interface ComisionesFilters {
  offset?: number;
  limit?: number;
  fechaDesde?: string;
  fechaHasta?: string;
  userId?: string;
}

export function useComisionesList(filters: ComisionesFilters) {
  return useQuery({
    queryKey: ['comisiones', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.offset !== undefined) params.set('offset', String(filters.offset));
      if (filters.limit !== undefined) params.set('limit', String(filters.limit));
      if (filters.fechaDesde) params.set('fechaDesde', filters.fechaDesde);
      if (filters.fechaHasta) params.set('fechaHasta', filters.fechaHasta);
      if (filters.userId) params.set('userId', filters.userId);
      const res = await apiClient.get<Paginated<ComisionResponse>>(
        `/admin/comisiones?${params.toString()}`,
      );
      return res.data;
    },
  });
}

export function useComisionesTotal(filters: { fechaDesde?: string; fechaHasta?: string }) {
  return useQuery({
    queryKey: ['comisiones', 'total', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.fechaDesde) params.set('fechaDesde', filters.fechaDesde);
      if (filters.fechaHasta) params.set('fechaHasta', filters.fechaHasta);
      const res = await apiClient.get<ComisionesTotalResponse>(
        `/admin/comisiones/total?${params.toString()}`,
      );
      return res.data;
    },
  });
}

export interface ComisionesExportFilters {
  fechaDesde?: string;
  fechaHasta?: string;
}

export async function exportComisionesReport(filters: ComisionesExportFilters): Promise<Blob> {
  const { data } = await apiClient.get<Blob>('/admin/comisiones/export', {
    params: filters,
    responseType: 'blob',
  });
  return data;
}

export async function exportComisionesUserReport(
  userId: string,
  filters: ComisionesExportFilters,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/comisiones/${userId}/detalle/export`, {
    params: filters,
    responseType: 'blob',
  });
  return data;
}

export function useExportComisiones() {
  return useMutation({
    mutationFn: (filters: ComisionesExportFilters) => exportComisionesReport(filters),
  });
}

export interface ComisionesUserExportVariables {
  userId: string;
  filters: ComisionesExportFilters;
}

export function useExportComisionesUser() {
  return useMutation({
    mutationFn: ({ userId, filters }: ComisionesUserExportVariables) =>
      exportComisionesUserReport(userId, filters),
  });
}

export function useComisionesDetail(
  userId: string | null,
  filters: Omit<ComisionesFilters, 'userId'>,
) {
  return useQuery({
    queryKey: ['comisiones', 'detail', userId, filters],
    enabled: userId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.offset !== undefined) params.set('offset', String(filters.offset));
      if (filters.limit !== undefined) params.set('limit', String(filters.limit));
      if (filters.fechaDesde) params.set('fechaDesde', filters.fechaDesde);
      if (filters.fechaHasta) params.set('fechaHasta', filters.fechaHasta);
      const res = await apiClient.get<Paginated<ComisionPagoDetalle>>(
        `/admin/comisiones/${userId}/detalle?${params.toString()}`,
      );
      return res.data;
    },
  });
}
